import React, { useState, useMemo, useEffect } from 'react';
import { Dropdown, Input, Button, Checkbox, Spin } from 'antd';
import { IconFont } from "../IconFont";
import './style.less';

export type FilterState = Record<string, string | string[]>;

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterColumnItem {
  title: string;
  dataIndex: string;
  valueType?: string;
  fieldProps?: {
    options?: FilterOption[] | (() => Promise<FilterOption[]>);
    mode?: 'single' | 'multiple';
    showSearch?: boolean;
  };
}

export interface FilterGroupConfig {
  title: string;
  valueType: 'group';
  columns: FilterColumnItem[];
}

export type FilterSchema = FilterColumnItem | FilterGroupConfig;

interface ModalFilterProps {
  className?: string;
  columns: FilterSchema[];
  value: FilterState;
  defaultSearchValue?: string;
  searchKey?: string | (() => string);
  onChange: (values: FilterState) => void;
  onReset?: () => void;
  placeholder?: string;
}

/* ===================== */
/* normalize（仅逻辑） */

/* ===================== */

interface NormalizedGroup {
  title: string;
  columns: FilterColumnItem[];
}

const normalizeColumns = (columns: FilterSchema[]): NormalizedGroup[] => {
  const result: NormalizedGroup[] = [];
  columns.forEach(item => {
    if ((item as FilterGroupConfig).valueType === 'group') {
      const g = item as FilterGroupConfig;
      result.push({ title: g.title, columns: g.columns });
    } else {
      result.push({ title: '', columns: [item as FilterColumnItem] });
    }
  });
  return result;
};

export function ModalFilter({
  className = '',
  columns = [],
  value = {},
  defaultSearchValue = '',
  searchKey = 'keyword',
  onChange,
  onReset,
  placeholder = "搜索和筛选"
}: ModalFilterProps) {

  /* ===================== */
  /* 状态模型（重构） */
  /* ===================== */

  const groups = useMemo(() => normalizeColumns(columns), [columns]);

  const firstKey = groups[0]?.columns[0]?.dataIndex || '';

  const [open, setOpen] = useState(false);
  const [activeMenuKey, setActiveMenuKey] = useState<string>(firstKey);

  // 草稿态（仅弹窗内）
  const [draft, setDraft] = useState<FilterState>({});
  const [draftSearch, setDraftSearch] = useState<string>(defaultSearchValue);
  const [textInput, setTextInput] = useState<string>('');

  // async options
  const [loading, setLoading] = useState(false);
  const [optionsCache, setOptionsCache] = useState<Record<string, FilterOption[]>>({});
  const [searchText, setSearchText] = useState<string>('');

  /* ===================== */
  /* 打开弹窗时初始化 */
  /* ===================== */

  useEffect(() => {
    if (!open) return;
    const key = typeof searchKey === 'function' ? searchKey() : searchKey;
    setDraft(value || {});
    setDraftSearch((value as any)?.[key] ?? '');
    // 如果当前激活字段是 text 类型且有值，回显到输入框
    const activeItem = groups.flatMap(g => g.columns).find(i => i.dataIndex === activeMenuKey);
    if (activeItem?.valueType === 'text' && value?.[activeMenuKey]) {
      setTextInput(value[activeMenuKey] as string);
    } else {
      setTextInput('');
    }
  }, [open]);

  // 切换菜单时更新文本输入
  useEffect(() => {
    const activeItem = groups.flatMap(g => g.columns).find(i => i.dataIndex === activeMenuKey);
    // 如果是 text 类型字段，从 draft 中获取值回显
    if (activeItem?.valueType === 'text' && draft[activeMenuKey]) {
      setTextInput(draft[activeMenuKey] as string);
    } else {
      setTextInput('');
    }
    // 切换菜单时清空搜索
    setSearchText('');
  }, [activeMenuKey]);

  /* ===================== */
  /* 当前激活项 */
  /* ===================== */

  const currentActiveItem = useMemo(() => {
    for (const g of groups) {
      const found = g.columns.find(i => i.dataIndex === activeMenuKey);
      if (found) return found;
    }
    return null;
  }, [groups, activeMenuKey]);

  /* ===================== */
  /* options 加载 */
  /* ===================== */

  const allOptions = useMemo(() => {
    if (!currentActiveItem) return [];
    const opts = currentActiveItem.fieldProps?.options;
    return Array.isArray(opts) ? opts : (optionsCache[currentActiveItem.dataIndex] || []);
  }, [currentActiveItem, optionsCache]);

  const displayOptions = useMemo(() => {
    if (!searchText || currentActiveItem?.valueType === 'text') return allOptions;
    return allOptions.filter(opt =>
        opt.label.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [allOptions, searchText, currentActiveItem]);

  useEffect(() => {
    if (!currentActiveItem) return;
    if (typeof currentActiveItem.fieldProps?.options !== 'function') return;
    if (optionsCache[currentActiveItem.dataIndex]) return;

    setLoading(true);
    currentActiveItem.fieldProps.options()
    .then(res => {
      setOptionsCache(prev => ({ ...prev, [currentActiveItem.dataIndex]: res }));
    })
    .finally(() => setLoading(false));
  }, [currentActiveItem]);

  /* ===================== */
  /* 提交（唯一出口） */
  /* ===================== */

  const commit = (next: FilterState, keyword = draftSearch) => {
    const key = typeof searchKey === 'function' ? searchKey() : searchKey;
    const result = { ...next };
    if (keyword) result[key] = keyword;
    else delete result[key];
    setDraft(result)
    onChange(result);
  };

  /* ===================== */
  /* 工具函数 */
  /* ===================== */

  const isSingleMode = (item: FilterColumnItem | null) =>
      item?.fieldProps?.mode !== 'multiple';

  const getSelectedLabels = (item: FilterColumnItem, val: any) => {
    if (!val) return '';

    // text类型直接返回值
    if (item.valueType === 'text') {
      return val;
    }

    const opts = Array.isArray(item.fieldProps?.options)
        ? item.fieldProps!.options
        : optionsCache[item.dataIndex] || [];

    if (Array.isArray(val)) {
      return val.map(v => opts.find(o => o.value === v)?.label || v).join('、');
    }
    return opts.find(o => o.value === val)?.label || val;
  };

  /* ===================== */
  /* 渲染（原样保留） */
  /* ===================== */

  const renderSidebar = () => (
      <div className="w-[240px] px-[16px] py-[8px] flex-shrink-0 bg-white overflow-y-auto border-b border-r">
        {groups.map((group, gIdx) => (
            <div key={gIdx}>
              {group.title && (
                  <div className="p-[8px] flex items-center">
                    <span className="text-default font-12 whitespace-nowrap">{group.title}</span>
                    <div className="h-[1px] bg-gray w-full ml-2"/>
                  </div>
              )}
              <ul className="list-none p-0 m-0">
                {group.columns.map(item => {
                  const isActive = activeMenuKey === item.dataIndex;
                  const val = draft[item.dataIndex];
                  const sideLabel = getSelectedLabels(item, val);

                  return (
                      <li
                          key={item.dataIndex}
                          onClick={() => setActiveMenuKey(item.dataIndex)}
                          className={`h-[36px] flex items-center justify-between px-[8px] my-[4px] font-medium cursor-pointer transition-colors
                  ${isActive ? 'bg-gray rounded-[8px] text-black' : ' text-gray hover:text-black'}`}
                      >
                        <span className="truncate mr-2 font-14">{item.title}</span>
                        <div className="flex items-center font-12">
                          <span className="truncate max-w-[80px] text-right text-black">{sideLabel}</span>
                          {isActive && <IconFont type="icon-chevron-right-small" className="ml-1 font-18"/>}
                        </div>
                      </li>
                  );
                })}
              </ul>
            </div>
        ))}
      </div>
  );

  const renderContent = () => {
    // text类型：输入框筛选
    if (currentActiveItem?.valueType === 'text') {
      return (
          <div className="flex-1 bg-white p-[16px] w-[240px] overflow-y-auto border-b relative">
            <span className='mb-2 flex'>搜索</span>
            <Input
                placeholder="输入后按回车保存"
                value={textInput}
                allowClear
                prefix={<IconFont type="icon-search" className='text-[18px]'/>}
                onChange={(e) => setTextInput(e.target.value)}
                onPressEnter={(e) => {
                  const value = (e.target as HTMLInputElement).value.trim();
                  // 同类型只能存在一条，直接替换
                  setDraft(prev => ({ ...prev, [activeMenuKey]: value }));
                  setTextInput('');
                }}
            />

          </div>
      );
    }

    // select类型：原有的checkbox逻辑
    const showSearch = currentActiveItem?.fieldProps?.showSearch;

    return (
        <div className="flex-1 bg-white w-[240px] flex flex-col border-b relative">
          {showSearch && (
              <div className="p-[16px] sticky top-0 bg-white z-10">
                <Input
                    placeholder="搜索选项"
                    value={searchText}
                    prefix={<IconFont type="icon-search" className='text-[18px]'/>}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                />
              </div>
          )}
          <div className={`flex-1 overflow-y-auto px-[16px] ${!showSearch ? 'pt-[16px]' : ''}`}>
            <Spin spinning={loading}>
              <div className="min-h-[100px]">
                {!isSingleMode(currentActiveItem) ? (
                    <Checkbox.Group
                        className="w-full flex flex-col gap-1"
                        value={(draft[activeMenuKey] as string[]) || []}
                        onChange={(vals) =>
                            setDraft(prev => ({ ...prev, [activeMenuKey]: vals as string[] }))
                        }
                    >
                      {displayOptions.map(opt => (
                          <Checkbox key={opt.value} value={opt.value} className="p-[8px]">
                            {opt.label}
                          </Checkbox>
                      ))}
                    </Checkbox.Group>
                ) : (
                    <>
                      {displayOptions.map(opt => {
                        const isChecked = draft[activeMenuKey] === opt.value;
                        return (
                            <Checkbox
                                key={opt.value}
                                checked={isChecked}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setDraft(prev => ({ ...prev, [activeMenuKey]: opt.value }));
                                  } else {
                                    setDraft(prev => {
                                      const { [activeMenuKey]: _, ...rest } = prev;
                                      return rest;
                                    });
                                  }
                                }}
                                className="p-[8px] w-full"
                            >
                              {opt.label}
                            </Checkbox>
                        );
                      })}
                    </>
                )}
              </div>
            </Spin>
          </div>
        </div>
    );
  };

  const renderTags = () => {
    const tags: React.ReactNode[] = [];
    groups.forEach(group => {
      group.columns.forEach(item => {
        const val = value[item.dataIndex];
        if (!val || (Array.isArray(val) && !val.length)) return;
        const labelsText = getSelectedLabels(item, val);
        tags.push(
            <div
                key={item.dataIndex}
                className="h-[24px] m-0.5 px-[8px] rounded-[2px] cursor-pointer flex items-center bg-gray font-12"
            >
            <span
                className="max-w-[420px] text-overflow"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveMenuKey(item.dataIndex);
                  setOpen(true);
                }}
            >
              <span className="text-gray">
                {group.title && `${group.title}·`}
                {item.title}:
              </span>
              <span className="text-black font-medium">{labelsText}</span>
            </span>
              <IconFont type="icon-chevron-down-small" className="font-18"/>
              <span
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = { ...value };
                    delete next[item.dataIndex];
                    commit(next);
                  }}
              >
              <IconFont type="icon-circle-x" className="ml-1 font-14"/>
            </span>
            </div>
        );
      });
    });
    return tags;
  };

  return (
      <div className={`modal-filter w-full ${className}`}>
        <Dropdown
            open={open}
            onOpenChange={setOpen}
            trigger={['click']}
            overlayClassName="modal-filter-dropdown"
            popupRender={() => (
                <div
                    className="bg-white flex flex-col radius-16 shadow-lg overflow-hidden border"
                    style={{ width: 480, height: 550 }}
                >
                  <div className="flex flex-1 overflow-hidden">
                    {renderSidebar()}
                    {renderContent()}
                  </div>
                  <div className="flex items-center gap-4 justify-between p-[16px] bg-white">
                    <Button
                        onClick={() => {
                          setDraft({})
                          setTextInput('');
                        }}
                        className="flex-1"
                    >
                      重置
                    </Button>
                    <Button
                        onClick={() => {
                          // 如果当前是 text 类型且输入框有值，自动添加到 draft
                          let finalDraft = draft;
                          if (currentActiveItem?.valueType === 'text' && textInput.trim()) {
                            finalDraft = { ...draft, [activeMenuKey]: textInput.trim() };
                          }
                          commit(finalDraft);
                          setOpen(false);
                        }}
                        type="primary"
                        className="flex-1"
                    >
                      筛选
                    </Button>
                  </div>
                </div>
            )}
        >
          <Input
              value={draftSearch}
              className="w-full cursor-pointer"
              prefix={
                <div className="flex-center">
                  <div className="flex items-center flex-wrap gap-1">
                    {renderTags()}
                  </div>
                  <IconFont type="icon-search" className="text-gray font-18 ml-1"/>
                </div>
              }
              suffix={
                <div className="flex-center font-14 text-gray font-medium">
                  <div className="w-[1px] h-[16px] bg-gray mr-[16px]"/>
                  <span
                      onClick={(e) => {
                        e.stopPropagation();
                        setDraft({});
                        setDraftSearch('');
                        commit({}, '');
                        onReset?.();
                      }}
                  >
                清除
              </span>
                </div>
              }
              placeholder={placeholder}
              onChange={(e) => setDraftSearch(e.target.value)}
              onBlur={() => {
                if (!open) {
                  commit(draft);
                }
              }}
              onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
          />
        </Dropdown>
      </div>
  );
}
